import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Row, Col, message, Empty, Table, Modal, Checkbox, Card, Space, Typography } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import axios from 'axios';
import config from '../config';
import { useAuth } from '../contexts/AuthContext'; // 导入useAuth钩子

const Search = () => {
  const [movies, setMovies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [pageSize, setPageSize] = useState(10);
  const [searchResult, setSearchResult] = useState(null);
  const [icons, setIcons] = useState({});  // 存储图标映射
  const [selectedMovies, setSelectedMovies] = useState([]); // 存储已选择的电影ID
  const { userInfo } = useAuth(); // 获取用户信息

  // 获取图标记录
  const fetchIcons = async () => {
    try {
      const response = await axios.get(`${config.apiBaseUrl}/api/icons`);
      // 将图标记录转换为以 diff_id 为键的对象
      const iconMap = response.data.reduce((acc, icon) => {
        acc[icon.diff_id] = icon;
        return acc;
      }, {});
      setIcons(iconMap);
    } catch (error) {
      console.error('获取图标记录失败：', error);
    }
  };

  // 获取用户已添加到代下载列表的电影
  const fetchUserDownloadCart = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return;
      }

      const response = await axios.get(
        `${config.apiBaseUrl}/api/download-cart/list`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      // 提取电影ID并更新状态
      if (Array.isArray(response.data)) {
        const movieIds = response.data.map(item => item.movie_id);
        setSelectedMovies(movieIds);
      }
    } catch (error) {
      console.error('获取代下载列表失败：', error);
    }
  };

  // 在组件加载时获取图标记录和用户代下载列表
  useEffect(() => {
    fetchIcons();
    fetchUserDownloadCart();
  }, []);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      // 验证是否至少填写了电影名称、IMDB编号或导演
      if (!values.name && !values.imdb && !values.dir) {
        message.error('请填写电影名称、IMDB编号或导演');
        setLoading(false);
        return;
      }

      console.log('发送搜索请求，参数：', values);
      const token = localStorage.getItem('token');
      console.log('当前token：', token);
      
      if (!token) {
        message.error('请先登录');
        return;
      }

      const response = await axios.get(`${config.apiBaseUrl}/api/movies/search`, {
        params: values,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('收到响应：', response.data);
      
      // 设置搜索结果
      setSearchResult(response.data);
      
      // 根据不同的返回类型处理数据
      if (response.data.type === 'list') {
        // 为每条记录添加唯一标识符，保留真实ID
        const moviesWithId = response.data.results.map((movie, index) => ({
          ...movie,
          frontendId: `movie-${movie.id}-${index}` // 使用真实ID构建前端唯一标识符
        }));
        setMovies(moviesWithId);
      } else if (response.data.type === 'imdb_not_found' || response.data.type === 'dir_not_found') {
        // 处理搜索无结果的情况
        setMovies([]);
      } else if (response.data.type === 'imdb_exact') {
        // 处理IMDB精确匹配的情况
        const mainMovie = response.data.movie;
        const otherMatches = response.data.other_matches || [];
        
        // 将主要电影和其他匹配结果合并，保留真实ID
        const allMovies = [
          { ...mainMovie, frontendId: `movie-${mainMovie.id}-0` },
          ...otherMatches.map((movie, index) => ({
            ...movie,
            frontendId: `movie-${movie.id}-${index + 1}`
          }))
        ];
        
        setMovies(allMovies);
      } else if (response.data.type === 'name_exact') {
        // 处理电影名称精确匹配的情况
        const mainMovie = response.data.movie;
        const otherMatches = response.data.other_matches || [];
        const fuzzyMatches = response.data.fuzzy_matches || [];
        
        // 将主要电影、其他精确匹配和模糊匹配结果合并，保留真实ID
        const allMovies = [
          { ...mainMovie, frontendId: `movie-${mainMovie.id}-0`, is_exact_match: true },
          ...otherMatches.map((movie, index) => ({
            ...movie,
            frontendId: `movie-${movie.id}-${index + 1}`
          })),
          ...fuzzyMatches.map((movie, index) => ({
            ...movie,
            frontendId: `movie-${movie.id}-fuzzy-${index}`
          }))
        ];
        
        setMovies(allMovies);
      } else if (response.data.type === 'name_not_found') {
        // 处理电影名称搜索无结果的情况
        setMovies([]);
      } else {
        // 对于其他AI回复类型，将主要电影和匹配的电影合并，保留真实ID
        const allMovies = [response.data.movie];
        if (response.data.other_matches) {
          allMovies.push(...response.data.other_matches);
        }
        const moviesWithId = allMovies.map((movie, index) => ({
          ...movie,
          frontendId: `movie-${movie.id}-${index}`
        }));
        setMovies(moviesWithId);
      }
      
    } catch (error) {
      console.error('搜索失败：', error);
      if (error.response) {
        console.error('错误响应：', error.response.data);
        if (error.response.status === 401) {
          message.error('请先登录');
          // 清除无效的token
          localStorage.removeItem('token');
        } else {
          message.error(error.response.data.error || '搜索失败');
        }
      } else if (error.request) {
        console.error('请求错误：', error.request);
        message.error('无法连接到服务器，请检查网络连接');
      } else {
        console.error('其他错误：', error.message);
        message.error('搜索失败，请稍后重试');
      }
    }
    setLoading(false);
  };

  // 重置所有搜索内容
  const resetAll = () => {
    form.resetFields();
    setMovies([]);
    setSearchResult(null);
  };

  // 处理分页大小变化
  const handlePageSizeChange = (current, size) => {
    setPageSize(size);
  };

  // 处理排序变化
  const handleTableChange = (pagination, filters, sorter) => {
    if (sorter.field === 'year') {
      // 如果没有排序状态，默认为正序
      const order = sorter.order || 'ascend';
      const sortedMovies = [...movies].sort((a, b) => {
        if (order === 'ascend') {
          return a.year - b.year;
        } else {
          return b.year - a.year;
        }
      });
      setMovies(sortedMovies);
    } else if (sorter.field === 'name') {
      // 处理电影名称排序
      const order = sorter.order || 'descend'; // 默认为降序
      const sortedMovies = [...movies].sort((a, b) => {
        if (order === 'ascend') {
          return a.name.localeCompare(b.name);
        } else {
          return b.name.localeCompare(a.name);
        }
      });
      setMovies(sortedMovies);
    } else if (sorter.field === 'size') {
      // 处理文件大小排序
      const order = sorter.order || 'ascend'; // 默认为升序
      const sortedMovies = [...movies].sort((a, b) => {
        // 将文件大小转换为MB进行比较
        const getSizeInMB = (sizeStr) => {
          if (!sizeStr) return Infinity;
          try {
            if (sizeStr.includes('GB')) {
              return parseFloat(sizeStr.replace('GB', '').trim()) * 1024;
            } else if (sizeStr.includes('MB')) {
              return parseFloat(sizeStr.replace('MB', '').trim());
            } else {
              return Infinity;
            }
          } catch (e) {
            return Infinity;
          }
        };
        
        const sizeA = getSizeInMB(a.size);
        const sizeB = getSizeInMB(b.size);
        
        if (order === 'ascend') {
          return sizeA - sizeB;
        } else {
          return sizeB - sizeA;
        }
      });
      setMovies(sortedMovies);
    }
  };

  const handleDownload = (url) => {
    // 创建一个选择对话框
    Modal.confirm({
      title: '下载安全提示',
      content: (
        <div>
          <p>请复制以下链接并在新标签页中打开：</p>
          <div style={{ 
            backgroundColor: '#f5f5f5', 
            padding: '12px', 
            borderRadius: '4px',
            marginTop: '8px',
            wordBreak: 'break-all'
          }}>
            <Typography.Text copyable>{url}</Typography.Text>
          </div>
          <p style={{ fontSize: '12px', color: '#f5222d', marginTop: '10px' }}>
            注意：本站不对外部内容负责，请确保您了解相关风险。
          </p>
        </div>
      ),
      okText: '关闭',
      cancelText: '取消',
      okButtonProps: { type: 'primary' },
      cancelButtonProps: { type: 'default' }
    });
  };

  const handleAddToCart = async (movieId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        message.error('请先登录');
        return;
      }

      // 检查是否已经选择
      if (selectedMovies.includes(movieId)) {
        // 已经选择了，无需重复添加
        return;
      }

      // 直接使用真实电影ID
      const realMovieId = movieId;

      const response = await axios.post(
        `${config.apiBaseUrl}/api/download-cart/add`,
        { movie_id: realMovieId },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // 添加到已选择列表
      setSelectedMovies(prev => [...prev, movieId]);
      message.success(response.data.message);
    } catch (error) {
      if (error.response) {
        message.error(error.response.data.message || '添加失败');
      } else {
        message.error('添加失败，请稍后重试');
      }
    }
  };

  // 表格列定义
  const columns = [
    {
      title: '电影名称',
      dataIndex: 'name',
      key: 'name',
      width: '25%',
      sorter: true,
      defaultSortOrder: searchResult && searchResult.sort_by === 'name' ? 
        (searchResult.sort_order === 'desc' ? 'descend' : 'ascend') : 'descend',
      render: (text, record) => (
        <span>
          {record.is_exact_match && searchResult && searchResult.type !== 'imdb_exact' && (
            <span style={{ 
              color: '#1890ff',
              marginRight: '8px',
              fontSize: '12px',
              padding: '2px 6px',
              background: '#e6f7ff',
              borderRadius: '4px'
            }}>
              精确
            </span>
          )}
          {text}
        </span>
      ),
    },
    {
      title: '导演',
      dataIndex: 'dir',
      key: 'dir',
      width: '15%',
    },
    {
      title: '年代',
      dataIndex: 'year',
      key: 'year',
      sorter: true,
      width: '8%',  // 减小年代列的宽度
      defaultSortOrder: searchResult && searchResult.sort_by === 'year' ? 
        (searchResult.sort_order === 'asc' ? 'ascend' : 'descend') : 'ascend',
    },
    {
      title: 'IMDB编号',
      dataIndex: 'imdb',
      key: 'imdb',
      width: '15%',
    },
    {
      title: '文件大小',
      dataIndex: 'size',
      key: 'size',
      width: '15%',
      sorter: true,
      defaultSortOrder: searchResult && searchResult.sort_by === 'size' ? 
        (searchResult.sort_order === 'asc' ? 'ascend' : 'descend') : undefined,
    },
    // 只有内部成员才显示下载链接列
    ...(userInfo?.Internal_members ? [{
      title: '下载链接',
      dataIndex: 'PageUrl',
      key: 'PageUrl',
      width: '20%',
      render: (text, record) => {
        const icon = icons[record.difference];
        return (
          <span>
            {icon && (
              <img 
                src={icon.icon_url}
                alt={icon.description || '图标'} 
                style={{ width: '16px', height: '16px', marginRight: '8px', verticalAlign: 'middle' }}
                onError={(e) => console.log('图片加载失败：', e)}
              />
            )}
            {text ? (
              <a 
                onClick={(e) => {
                  e.preventDefault();
                  handleDownload(text);
                }}
                style={{ cursor: 'pointer' }}
              >
                点击下载
              </a>
            ) : null}
          </span>
        );
      },
    }] : []),
    {
      title: <span style={{ fontWeight: '600', color: '#1890ff' }}>代下载</span>,
      key: 'download_proxy',
      width: '120px',
      render: (_, record) => {
        const isSelected = selectedMovies.includes(record.id);
        return (
          <Button 
            type={isSelected ? "primary" : "default"}
            shape="round"
            size="small"
            icon={isSelected ? <CheckOutlined /> : null}
            style={{ 
              width: '70px',
              height: '32px',
              transition: 'all 0.3s',
              backgroundColor: isSelected ? '#1890ff' : 'white',
              borderColor: isSelected ? '#1890ff' : '#d9d9d9',
              color: isSelected ? 'white' : '#666',
              boxShadow: isSelected ? '0 2px 6px rgba(24, 144, 255, 0.25)' : 'none',
              fontWeight: isSelected ? '500' : 'normal',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
            onClick={() => handleAddToCart(record.id)}
          >
            {isSelected ? '已选' : '选择'}
          </Button>
        );
      },
    }
  ];

  return (
    <div>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        style={{ maxWidth: 1200, margin: '0 auto' }}
      >
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item
              name="name"
              label="电影名称"
              rules={[
                {
                  validator: (_, value) => {
                    if (!value && !form.getFieldValue('imdb') && !form.getFieldValue('dir')) {
                      return Promise.reject('请填写电影名称、IMDB编号或导演');
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <Input placeholder="输入电影名称（支持模糊搜索）" allowClear />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="imdb"
              label="IMDB编号"
              rules={[
                {
                  validator: (_, value) => {
                    if (!value && !form.getFieldValue('name') && !form.getFieldValue('dir')) {
                      return Promise.reject('请填写电影名称、IMDB编号或导演');
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <Input 
                placeholder="输入IMDB编号（如tt0000001）" 
                allowClear 
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="year"
              label="年代"
            >
              <Input 
                placeholder="输入年代" 
                allowClear 
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="dir"
              label="导演"
              rules={[
                {
                  validator: (_, value) => {
                    if (!value && !form.getFieldValue('name') && !form.getFieldValue('imdb')) {
                      return Promise.reject('请填写电影名称、IMDB编号或导演');
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <Input placeholder="输入导演姓名" allowClear />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading}>
                搜索
              </Button>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item>
              <Button onClick={resetAll}>
                重置所有
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <div style={{ marginTop: '20px' }}>
        {searchResult && searchResult.message && (
          <Card 
            style={{ 
              marginBottom: '20px', 
              backgroundColor: '#1e1e2e',
              color: 'white',
              borderRadius: '8px'
            }}
            styles={{
              body: {
                color: 'white',
                padding: '16px'
              }
            }}
          >
            <Typography.Paragraph 
              style={{ 
                margin: 0,
                whiteSpace: 'pre-wrap',
                fontFamily: 'monospace',
                color: 'white'
              }}
            >
              {searchResult.message}
            </Typography.Paragraph>
            {searchResult.is_combined_search && (
              <div style={{ marginTop: '10px', fontSize: '12px', color: '#aaa' }}>
                电影名称支持模糊查询，不区分大小写。
              </div>
            )}
            <div style={{ 
              marginTop: '10px', 
              padding: '8px 12px', 
              backgroundColor: 'rgba(255, 255, 255, 0.1)', 
              borderRadius: '4px',
              fontSize: '15px',
              color: '#ffb74d'
            }}>
              💡 提示: 若您需要代下载，请点击最右侧"选择"按钮后，点击左上方"代下载管理"进行提交
            </div>
          </Card>
        )}
        
        {movies.length > 0 ? (
          <>
            {searchResult && searchResult.type === 'name_exact' && (
              <>
                {searchResult.fuzzy_matches && searchResult.fuzzy_matches.length > 0 && (
                  <div style={{ marginTop: '20px', marginBottom: '16px', color: '#666' }}>
                    模糊匹配结果：
                  </div>
                )}
              </>
            )}
            <Table
              columns={columns}
              dataSource={movies}
              rowKey="frontendId"
              pagination={{
                pageSize: pageSize,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `共 ${total} 条记录`,
                onShowSizeChange: handlePageSizeChange,
                pageSizeOptions: ['10', '20', '50', '100']
              }}
              loading={loading}
              onChange={handleTableChange}
            />
          </>
        ) : (
          <Empty
            description={
              searchResult && searchResult.is_combined_search 
                ? "未找到同时满足所有条件的电影" 
                : "未找到相关电影信息"
            }
            style={{ margin: '40px 0' }}
          />
        )}
      </div>
    </div>
  );
};

export default Search; 